import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageLayout from "@/components/layout/PageLayout";
import { Section } from "@/components/layout/Sections";
import ContactForm from "@/components/ContactForm";
import api, { mediaUrl } from "@/lib/api";
import { Calendar, MapPin, Users, ArrowLeft, Bike, Link as LinkIcon, Clock, ShieldCheck } from "lucide-react";

export default function EventoOffRoad() {
  const [posterUrl, setPosterUrl] = useState("");

  useEffect(() => {
    api.get("/settings/event-poster")
      .then((r) => setPosterUrl(r.data?.url || ""))
      .catch(() => setPosterUrl(""));
  }, []);

  const copyEventLink = async () => {
    const url = window.location.href.split("#")[0];
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link evento copiato — pronto da condividere");
    } catch {
      const tmp = document.createElement("input");
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand("copy"); toast.success("Link copiato"); }
      catch { toast.error(`Copia manualmente: ${url}`); }
      finally { document.body.removeChild(tmp); }
    }
  };

  return (
    <PageLayout>
      {/* Hero cinematografico — solo nero pulito, niente placeholder */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] w-full overflow-hidden bg-gray-900">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 40%, rgba(255,102,0,0.15) 0%, transparent 60%), linear-gradient(180deg, #111 0%, #000 100%)" }} />
        <div className="grain absolute inset-0" />

        <div className="relative nsa-container h-full min-h-[70vh] md:min-h-[85vh] flex flex-col justify-end pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/moto"
              className="label-eyebrow inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft size={14} /> Torna a Moto
            </Link>
            <span className="label-eyebrow text-[color:var(--nsa-orange)] tracking-[0.3em] text-xs md:text-sm">
              Save the date · Evento speciale
            </span>
            <h1 className="font-display uppercase text-white text-4xl md:text-7xl leading-[0.9] mt-4 drop-shadow-2xl">
              Off Road Day
            </h1>
            <p className="mt-4 text-white/90 text-lg md:text-2xl font-body max-w-xl">
              Test gratuito NSA Motors
            </p>
            <div className="mt-8 flex flex-wrap gap-4 md:gap-6 text-white">
              <div className="flex items-center gap-2 border border-white/30 px-4 py-2 backdrop-blur-sm bg-black/30">
                <Calendar size={16} className="text-[color:var(--nsa-orange)]" />
                <span className="label-eyebrow tracking-[0.2em] text-sm">Domenica 4 Ottobre 2026</span>
              </div>
              <div className="flex items-center gap-2 border border-white/30 px-4 py-2 backdrop-blur-sm bg-black/30">
                <MapPin size={16} className="text-[color:var(--nsa-orange)]" />
                <span className="label-eyebrow tracking-[0.2em] text-sm">Pista Enduro Scalvina · Ponte Nossa (BG)</span>
              </div>
              <div className="flex items-center gap-2 border border-white/30 px-4 py-2 backdrop-blur-sm bg-black/30">
                <Bike size={16} className="text-[color:var(--nsa-orange)]" />
                <span className="label-eyebrow tracking-[0.2em] text-sm">KTM · Husqvarna Motorcycles</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Locandina evento — formato 4:5, caricabile da admin */}
      {posterUrl && (
        <Section>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md mx-auto"
            data-testid="event-poster"
          >
            <div className="aspect-[4/5] w-full overflow-hidden bg-gray-100 border border-gray-200">
              <img
                src={mediaUrl(posterUrl)}
                alt="Locandina Off Road Day NSA Motors"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </Section>
      )}

      {/* Info evento */}
      <Section>
        <div className="grid md:grid-cols-3 gap-10 md:gap-12">
          <div className="md:col-span-2">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">🔥 Vivi l'off road al limite</span>
            <h2 className="font-display uppercase text-3xl md:text-5xl mt-3 leading-[0.95]">
              Off Road Day<br />NSA Motors
            </h2>
            <div className="mt-8 space-y-6 text-gray-700 leading-relaxed text-lg">
              <p>
                Ti invitiamo a vivere insieme una giornata immersa nell'off road:
                prove in pista, test ride sulla gamma <strong>KTM</strong> e
                <strong> Husqvarna Motorcycles</strong>, in compagnia dello staff
                tecnico di NSA Motors.
              </p>
              <p>
                Domenica 4 ottobre ti aspettiamo alla <strong>Pista Enduro Scalvina
                – Ponte Nossa (BG)</strong> per il <strong>test gratuito della gamma
                2027 KTM &amp; Husqvarna Off Road</strong>. Scendi in pista e prova
                i nuovi modelli:
              </p>

              {/* Lista moto disponibili */}
              <ul className="grid sm:grid-cols-2 gap-3 not-prose">
                {[
                  "KTM XC-W 125",
                  "Husqvarna TE 125 / 250 / 300",
                  "KTM 300 EXC e 350 EXC-F",
                  "Husqvarna FE 250",
                ].map((bike) => (
                  <li
                    key={bike}
                    className="flex items-center gap-3 bg-gray-50 border-l-4 border-[color:var(--nsa-orange)] px-4 py-3"
                  >
                    <Bike size={18} className="text-[color:var(--nsa-orange)] shrink-0" />
                    <span className="font-display uppercase text-base tracking-wide text-gray-900">
                      {bike}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Blocco info logistiche */}
              <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <div className="border border-gray-200 p-4">
                  <div className="flex items-center gap-2 text-[color:var(--nsa-orange)] mb-1">
                    <Calendar size={16} />
                    <span className="label-eyebrow text-[10px] tracking-widest">Data</span>
                  </div>
                  <div className="font-display uppercase text-base leading-tight">Domenica 4 ottobre</div>
                </div>
                <div className="border border-gray-200 p-4">
                  <div className="flex items-center gap-2 text-[color:var(--nsa-orange)] mb-1">
                    <Clock size={16} />
                    <span className="label-eyebrow text-[10px] tracking-widest">Orario</span>
                  </div>
                  <div className="font-display uppercase text-base leading-tight">Dalle 9:00 alle 14:00</div>
                </div>
                <div className="border border-gray-200 p-4">
                  <div className="flex items-center gap-2 text-[color:var(--nsa-orange)] mb-1">
                    <MapPin size={16} />
                    <span className="label-eyebrow text-[10px] tracking-widest">Location</span>
                  </div>
                  <div className="font-display uppercase text-base leading-tight">
                    Pista Enduro Scalvina<br />
                    <span className="text-sm text-gray-600 normal-case tracking-normal">Ponte Nossa (BG)</span>
                  </div>
                </div>
              </div>

              {/* Requisiti / Licenza */}
              <div className="mt-4 border-2 border-[color:var(--nsa-orange)] bg-[color:var(--nsa-orange)]/5 p-6">
                <div className="flex items-center gap-2 text-[color:var(--nsa-orange)] mb-3">
                  <ShieldCheck size={18} />
                  <span className="label-eyebrow tracking-widest text-xs">Requisiti per partecipare</span>
                </div>
                <p className="text-gray-800 leading-relaxed text-base">
                  Per partecipare è necessario avere:
                </p>
                <ul className="mt-3 space-y-2 text-gray-800 text-base leading-relaxed">
                  <li className="flex gap-2">
                    <span className="text-[color:var(--nsa-orange)] font-bold">→</span>
                    Abbigliamento <strong>off road adeguato</strong> (casco, stivali, protezioni)
                  </li>
                  <li className="flex gap-2">
                    <span className="text-[color:var(--nsa-orange)] font-bold">→</span>
                    <span>
                      <strong>Licenza/tessera FMI</strong> (NO Member) <em>oppure</em>
                      <strong> tessera AICS</strong>
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-center text-[color:var(--nsa-orange)] font-display uppercase text-xl md:text-2xl tracking-wide pt-2">
                ⚡ Test gratuito. Ti aspettiamo in pista!
              </p>
            </div>
          </div>

          <div className="md:col-span-1">
            <div className="card-flat p-6 bg-gray-50 border-l-4 border-[color:var(--nsa-orange)] sticky top-24">
              <span className="label-eyebrow text-[color:var(--nsa-orange)]">
                <Users size={14} className="inline mr-1 -mt-1" /> Partecipazione libera
              </span>
              <h3 className="font-display uppercase text-xl mt-2">Per informazioni</h3>
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                Non è richiesta iscrizione: puoi presentarti direttamente sul posto.
                Per informazioni o dettagli, contattaci compilando il form qui sotto.
              </p>
              <a
                href="#form-evento"
                className="mt-5 block btn-orange label-eyebrow text-center py-3"
                data-testid="event-cta-form"
              >
                Contattaci
              </a>
              <button
                onClick={copyEventLink}
                className="mt-3 w-full border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-3 label-eyebrow text-sm transition-colors inline-flex items-center gap-2 justify-center"
                data-testid="event-share-link"
              >
                <LinkIcon size={14} /> Copia link evento
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Form contatto */}
      <Section tone="dark">
        <div id="form-evento" className="grid md:grid-cols-5 gap-10 md:gap-16 items-start">
          <div className="md:col-span-2 text-white">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Per informazioni</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.95]">
              Contattaci<br />per l'evento
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-md">
              Lasciaci i tuoi dati e la tua richiesta: ti ricontatteremo con tutte le
              informazioni sull'evento off-road di domenica 4 ottobre 2026.
            </p>
          </div>
          <div className="md:col-span-3">
            <ContactForm
              section="moto"
              defaultSubject="Evento Off Road — 4 ottobre 2026"
              submitLabel="Invia richiesta"
              tid="event-contact-form"
            />
          </div>
        </div>
      </Section>
    </PageLayout>
  );
}
